import { AppForm } from '../../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import {
  bothRequired,
  CustomValidator,
  fromToDate,
  fromToYears
} from '../../../../../../../common/forms/custom.validators';
import { calcDaysBetweenDates } from '../../../../../../../services/helpers';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../../openapi';
import {
  RoadControlsForm,
  RoadControlsFormValue
} from '../../../../common/components/road-controls-set/road-controls.form';
import { environment } from '../../../../../../../../environments/environment';
import {
  ExposedMapDictionary,
  ExposedMapDictRate, getRatesAreaByCodes
} from '../../../../../../../common/exposed-map/exposed-map.component';

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): LocationOfDevicesUnrelatedToRoadStageFormValue {
  return {
    id: v?.uuid || '',
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,
    nextYearsFrom: v?.nextYearsFrom?.toString() || '',
    nextYearsTo: v?.nextYearsTo?.toString() || '',
    yearsCount: v?.yearsCount?.toString() || '',

    infrastructureTelecommunicationOnly: !!v?.infrastructureTelecommunicationOnly,

    geometry: v?.geometry || '',
    roadName: v?.roadName || '',
    roadNumber: v?.roadNumber || '',
    street: v?.street || '',
    parcels: v?.parcels || [],

    bridgeStructureWidth: (v?.bridgeStructureWidth || 0).toFixed(2).replace('.', ','),
    bridgeStructureLength: (v?.bridgeStructureLength || 0).toFixed(2).replace('.', ','),
    bridgeStructureArea: (v?.bridgeStructureArea || 0).toFixed(2).replace('.', ','),

    outsideBridgeStructureWidth: (v?.outsideBridgeStructureWidth || 0).toFixed(2).replace('.', ','),
    outsideBridgeStructureLength: (v?.outsideBridgeStructureLength || 0).toFixed(2).replace('.', ','),
    outsideBridgeStructureArea: (v?.outsideBridgeStructureArea || 0).toFixed(2).replace('.', ','),

    infrastructureEquipmentWidth: (v?.infrastructureEquipmentWidth || 0).toFixed(2).replace('.', ','),
    infrastructureEquipmentLength: (v?.infrastructureEquipmentLength || 0).toFixed(2).replace('.', ','),
    infrastructureEquipmentArea: (v?.infrastructureEquipmentArea || 0).toFixed(2).replace('.', ',')
  };
}

export interface LocationOfDevicesUnrelatedToRoadStageFormValue extends RoadControlsFormValue {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  nextYearsFrom: string;
  nextYearsTo: string;
  yearsCount: string;

  infrastructureTelecommunicationOnly: boolean;

  geometry: string;

  parcels: ParcelAppEntityParcelWrite[];

  bridgeStructureWidth: string;
  bridgeStructureLength: string;
  bridgeStructureArea?: string;

  outsideBridgeStructureWidth: string;
  outsideBridgeStructureLength: string;
  outsideBridgeStructureArea?: string;

  infrastructureEquipmentWidth: string;
  infrastructureEquipmentLength: string;
  infrastructureEquipmentArea?: string;
}

export class LocationOfDevicesUnrelatedToRoadStageForm extends RoadControlsForm {

  public get yearList(): string[] {
    let result = [];
    const fullYear = new Date().getFullYear() + 1;
    for (let i = fullYear; i < (fullYear + 10); i++) {
      result.push(`${i}`);
    }
    return result;
  }

  constructor() {
    super({
      id: new FormControl('', { nonNullable: true }),
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl({ value: '', disabled: true }, [Validators.maxLength(255)]),

      nextYearsFrom: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(255)]
      }),
      nextYearsTo: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(255)]
      }),
      yearsCount: new FormControl({ value: '', disabled: true }, {
        validators: [Validators.maxLength(255)]
      }),

      infrastructureTelecommunicationOnly: new FormControl(false, { nonNullable: true }),

      geometry: new FormControl(null, environment.production ? Validators.required : []),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      bridgeStructureWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      bridgeStructureLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      bridgeStructureArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      outsideBridgeStructureWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      outsideBridgeStructureLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      outsideBridgeStructureArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      infrastructureEquipmentWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      infrastructureEquipmentLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      infrastructureEquipmentArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      )

    }, {
      validators: [
        fromToDate('startDate', 'endDate'),
        fromToYears('nextYearsFrom', 'nextYearsTo'),
        bothRequired('bridgeStructureWidth', 'bridgeStructureLength'),
        bothRequired('outsideBridgeStructureWidth', 'outsideBridgeStructureLength')
      ]
    });
  }

  public get infrastructureTelecommunicationOnly(): FormControl {
    return this.get('infrastructureTelecommunicationOnly') as FormControl;
  }

  public get yearsCount(): FormControl {
    return this.get('yearsCount') as FormControl;
  }

  public calcYearsCount(): void {
    const from = +this.value.nextYearsFrom;
    const to = +this.value.nextYearsTo;
    if (from && to && (+from <= +to)) {
      this.yearsCount.setValue(`${to - from + 1}`);
    } else {
      this.yearsCount.setValue('');
    }
  }

  private calcArea(width?: string, length?: string): string {
    if (!width || !length) {
      return '';
    }
    width = (width || '0').replaceAll(',', '.').replaceAll(' ', '');
    length = (length || '0').replaceAll(',', '.').replaceAll(' ', '');
    return (Number(width) * Number(length)).toFixed(2).replaceAll('.', ',');
  }

  public calcBridgeStructureArea(): void {
    this.bridgeStructureArea.setValue(
      this.calcArea(this.bridgeStructureWidth.value, this.bridgeStructureLength.value)
    );
  }

  public calcOutsideBridgeStructureArea(): void {
    this.outsideBridgeStructureArea.setValue(
      this.calcArea(this.outsideBridgeStructureWidth.value, this.outsideBridgeStructureLength.value)
    );
  }

  public calcInfrastructureEquipmentArea(): void {
    this.infrastructureEquipmentArea.setValue(
      this.calcArea(this.infrastructureEquipmentWidth.value, this.infrastructureEquipmentLength.value)
    );
  }

  public get bridgeStructureWidth(): FormControl {
    return this.get('bridgeStructureWidth') as FormControl;
  }

  public get bridgeStructureLength(): FormControl {
    return this.get('bridgeStructureLength') as FormControl;
  }

  public get bridgeStructureArea(): FormControl {
    return this.get('bridgeStructureArea') as FormControl;
  }

  public get canClearBridgeStructureControls(): boolean {
    return this.bridgeStructureWidth.value || this.bridgeStructureLength.value || this.bridgeStructureArea.value;
  }

  public clearBridgeStructureControls(): void {
    this.bridgeStructureWidth.setValue('', { emitEvent: false });
    this.bridgeStructureLength.setValue('', { emitEvent: false });
    this.bridgeStructureArea.setValue('', { emitEvent: false });
  }

  public get outsideBridgeStructureWidth(): FormControl {
    return this.get('outsideBridgeStructureWidth') as FormControl;
  }

  public get outsideBridgeStructureLength(): FormControl {
    return this.get('outsideBridgeStructureLength') as FormControl;
  }

  public get outsideBridgeStructureArea(): FormControl {
    return this.get('outsideBridgeStructureArea') as FormControl;
  }

  public get canClearOutsideBridgeStructureControls(): FormControl {
    return this.outsideBridgeStructureWidth.value || this.outsideBridgeStructureLength.value || this.outsideBridgeStructureArea.value;
  }

  public clearOutsideBridgeStructureControls(): void {
    this.outsideBridgeStructureWidth.setValue('', { emitEvent: false });
    this.outsideBridgeStructureLength.setValue('', { emitEvent: false });
    this.outsideBridgeStructureArea.setValue('', { emitEvent: false });
  }

  public get infrastructureEquipmentWidth(): FormControl {
    return this.get('infrastructureEquipmentWidth') as FormControl;
  }

  public get infrastructureEquipmentLength(): FormControl {
    return this.get('infrastructureEquipmentLength') as FormControl;
  }

  public get infrastructureEquipmentArea(): FormControl {
    return this.get('infrastructureEquipmentArea') as FormControl;
  }

  public get canClearInfrastructureEquipmentControls(): boolean {
    return this.infrastructureEquipmentWidth?.value || this.infrastructureEquipmentLength?.value || this.infrastructureEquipmentArea?.value;
  }

  public clearInfrastructureEquipmentControls(): void {
    this.infrastructureEquipmentWidth?.setValue('', { emitEvent: false });
    this.infrastructureEquipmentLength?.setValue('', { emitEvent: false });
    this.infrastructureEquipmentArea?.setValue('', { emitEvent: false });
  }

  public onLoadExposedMapDict(v: ExposedMapDictionary): void {

    const bridgeStructureArea = getRatesAreaByCodes(v.rates, [257, 263]);
    const outsideBridgeStructureArea = getRatesAreaByCodes(v.rates, [258, 264]);
    const infrastructureEquipmentArea = getRatesAreaByCodes(v.rates, [262]);

    if (this.infrastructureTelecommunicationOnly.value) {
      this.clearBridgeStructureControls();
      this.clearInfrastructureEquipmentControls();
      this.clearOutsideBridgeStructureControls();

      this.infrastructureEquipmentArea.setValue((bridgeStructureArea + outsideBridgeStructureArea) || infrastructureEquipmentArea || '');
    } else {
      this.clearBridgeStructureControls();
      this.clearInfrastructureEquipmentControls();
      this.clearOutsideBridgeStructureControls();

      if (bridgeStructureArea) {
        this.bridgeStructureWidth.setValue('', { emitEvent: false });
        this.bridgeStructureLength.setValue('', { emitEvent: false });
        this.bridgeStructureArea.setValue(bridgeStructureArea);
      }

      if (outsideBridgeStructureArea) {
        this.outsideBridgeStructureWidth.setValue('', { emitEvent: false });
        this.outsideBridgeStructureLength.setValue('', { emitEvent: false });
        this.outsideBridgeStructureArea.setValue(outsideBridgeStructureArea);
      }
    }
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public override get valid(): boolean {
    return super.valid && (!!this.bridgeStructureArea.value || !!this.outsideBridgeStructureArea.value || !!this.infrastructureEquipmentArea.value);
  }

  public get values(): LocationOfDevicesUnrelatedToRoadStageFormValue {
    return {
      id: this.value.id,
      startDate: this.value.startDate,
      endDate: this.value.endDate,
      dayQuantity: this.dayQuantity.value,

      nextYearsFrom: this.value.nextYearsFrom,
      nextYearsTo: this.value.nextYearsTo,
      yearsCount: this.yearsCount.value,

      infrastructureTelecommunicationOnly: this.infrastructureTelecommunicationOnly.value,

      geometry: this.geometry.value,

      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.street.value,

      parcels: this.parcels.value,

      bridgeStructureWidth: this.bridgeStructureWidth.value,
      bridgeStructureLength: this.bridgeStructureLength.value,
      bridgeStructureArea: this.bridgeStructureArea.value,

      outsideBridgeStructureWidth: this.outsideBridgeStructureWidth.value,
      outsideBridgeStructureLength: this.outsideBridgeStructureLength.value,
      outsideBridgeStructureArea: this.outsideBridgeStructureArea.value,

      infrastructureEquipmentWidth: this.infrastructureEquipmentWidth.value,
      infrastructureEquipmentLength: this.infrastructureEquipmentLength.value,
      infrastructureEquipmentArea: this.infrastructureEquipmentArea.value
    };
  }
}
